import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { TopBar } from './components/TopBar';
import { SideDrawer } from './components/SideDrawer';
import { TaskReview } from './components/TaskReview';
import { ListCreator } from './components/ListCreator';
import { SummaryConfig } from './components/SummaryConfig';
import { SourceRules } from './components/SourceRules';
import { VoiceView } from './components/VoiceView';
import { ChatView } from './components/ChatView';
import { CodeView } from './components/CodeView';
import { LibraryView } from './components/LibraryView';
import { DataView } from './components/DataView';
import { FeedView as MessagesView } from './components/FeedView';
import { FeedContentView } from './components/FeedContentView';
import { SkillsView } from './components/SkillsView';
import { IntegrationsView } from './components/IntegrationsView';
import { SettingsView } from './components/SettingsView';
import { ActionsView } from './components/ActionsView';
import { ArtifactsView } from './components/ArtifactsView';
import { TranscriptsView } from './components/TranscriptsView';
import { AgentsView } from './components/AgentsView';
import { BrowserView } from './components/BrowserView';
import { DocumentsView } from './components/DocumentsView';
import { WorkflowsView } from './components/WorkflowsView';
import { SyncHistoryView } from './components/SyncHistoryView';
import { CommandPalette } from './components/CommandPalette';
import { HelpSheet } from './components/HelpSheet';
import { AIChatPanel } from './components/AIChatPanel';
const ROUTES: Record<string, React.FC> = {
  voice: VoiceView,
  tasks: TaskReview,
  lists: ListCreator,
  summaries: SummaryConfig,
  'source-rules': SourceRules,
  library: LibraryView,
  data: DataView,
  messages: MessagesView,
  feed: FeedContentView,
  chat: ChatView,
  code: CodeView,
  skills: SkillsView,
  integrations: IntegrationsView,
  agents: AgentsView,
  actions: ActionsView,
  artifacts: ArtifactsView,
  transcripts: TranscriptsView,
  browser: BrowserView,
  documents: DocumentsView,
  workflows: WorkflowsView,
  settings: SettingsView,
  'sync-history': SyncHistoryView
};
function getHashRoute(): string {
  const hash = window.location.hash.replace('#/', '').replace('#', '');
  return hash || 'voice';
}
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}
export function App() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeView, setActiveView] = useState(getHashRoute);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [helpOpen, setHelpOpenState] = useState(false);
  const [chatOpen, setChatOpenState] = useState(false);
  const isMobile = useIsMobile();
  const openChat = () => {
    setChatOpenState(true);
    if (isMobile) setHelpOpenState(false);
  };
  const toggleHelp = () => {
    setHelpOpenState(prev => {
      const next = !prev;
      if (next && isMobile) setChatOpenState(false);
      return next;
    });
  };
  const toggleChat = () => {
    setChatOpenState(prev => {
      const next = !prev;
      if (next && isMobile) setHelpOpenState(false);
      return next;
    });
  };
  useEffect(() => {
    const onHashChange = () => setActiveView(getHashRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(prev => !prev);
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'i') {
        e.preventDefault();
        toggleChat();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);
  const handleNavigate = (view: string) => {
    window.location.hash = `#/${view}`;
    setActiveView(view);
    if (isMobile) setIsDrawerOpen(false);
  };
  const ViewComponent = ROUTES[activeView];
  const drawerReserved = isDrawerOpen && !isMobile ? 280 : 0;
  const chatReserved = chatOpen && !isMobile ? 400 : 0;
  const helpCompressed = helpOpen && !isMobile;
  return <div className="w-full min-h-screen bg-[#141418] font-heading" data-id="element-0">
      <TopBar isDrawerOpen={isDrawerOpen} onToggleDrawer={() => setIsDrawerOpen(!isDrawerOpen)} onToggleHelp={toggleHelp} onToggleChat={toggleChat} helpOpen={helpOpen} chatOpen={chatOpen} data-id="element-1" />
      <SideDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} activeView={activeView} onNavigate={handleNavigate} isMobile={isMobile} data-id="element-2" />
      <motion.main animate={{
      width: `calc(100% - ${drawerReserved}px - ${chatReserved}px)`,
      height: helpCompressed ? 'calc(55vh - 56px)' : 'calc(100vh - 56px)',
      marginLeft: drawerReserved
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className="pt-14 overflow-y-auto" style={{
      boxSizing: 'border-box'
    }} data-id="element-3">
        {ViewComponent ? <ViewComponent data-id="element-4" /> : <div className="flex items-center justify-center min-h-[calc(100vh-56px)]" data-id="element-5">
            <span className="text-[#8a8680]/50 text-[15px] capitalize" data-id="element-6">
              {activeView.replace('-', ' ')}
            </span>
          </div>}
      </motion.main>
      <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} onNavigate={handleNavigate} onOpenChat={openChat} data-id="element-7" />
      <HelpSheet isOpen={helpOpen} onClose={() => setHelpOpenState(false)} route={activeView} drawerOpen={isDrawerOpen && !isMobile} chatOpen={chatOpen && !isMobile} isMobile={isMobile} data-id="element-8" />
      <AIChatPanel isOpen={chatOpen} onClose={() => setChatOpenState(false)} route={activeView} isMobile={isMobile} data-id="element-9" />
    </div>;
}